import i18n from '@/i18n/i18n';

/**
 * Calculates duration between two dates as a human-readable string
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns A localized duration string (e.g. "25 years", "6 months") or null if startDate is invalid
 */
export function duration(startDate: Date, endDate: Date): string | null {
  if (!startDate || isNaN(startDate.getTime())) {
    return null;
  }

  // Calculate years
  let years = endDate.getFullYear() - startDate.getFullYear();
  const monthDiff = endDate.getMonth() - startDate.getMonth();
  const dayDiff = endDate.getDate() - startDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    years--;
  }

  // If duration is 1 year or more, return years
  if (years >= 1) {
    return i18n.t('common:duration.years', { count: years });
  }

  // Calculate months for durations under 1 year
  let months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
  if (dayDiff < 0) {
    months--;
  }

  // If duration is 1 month or more, return months
  if (months >= 1) {
    return i18n.t('common:duration.months', { count: months });
  }

  // Calculate days for durations under 1 month
  const timeDiff = endDate.getTime() - startDate.getTime();
  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

  if (days >= 1) {
    return i18n.t('common:duration.days', { count: days });
  }

  return i18n.t('common:duration.lessThanOneDay');
}
